import { z } from 'zod';

const transformNullToUndefined = z.null().transform(() => undefined);

const getNumberFromString = (value: string, context: z.RefinementCtx) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Not a number',
    });
    return z.NEVER;
  }
  return parsed;
};

const getStringFromNumber = (value: number) => {
  const parsed = value.toString();
  return parsed;
};

export const optionalObject = <T extends z.ZodTypeAny>(objectSchema: T) =>
  z.union([objectSchema, transformNullToUndefined, z.undefined()]);

// takes a string or number or null and returns a number or undefined
export const optionalNumber = z.union([
  z.number(),
  z.string().transform(getNumberFromString),
  transformNullToUndefined,
  z.undefined(),
]);

export const transformNumber = z.union([
  z.number(),
  z.string().transform(getNumberFromString),
]);

export const optionalString = z.union([
  z.string(),
  transformNullToUndefined,
  z.undefined(),
]);

// includes logic for strings being sent as 'True' or 'False'
export const optionalBoolean = z.union([
  z.string().transform((value) => value === 'True'),
  z.boolean(),
  transformNullToUndefined,
  z.undefined(),
]);

export const optionalNumberToString = z.union([
  z.string(),
  z.number().transform(getStringFromNumber),
  transformNullToUndefined,
]);

// check if date is within baseDate and baseDate + numberOfDaysToAdd (inclusive)
export const dateWithinDaysRange = (
  baseDate: Date,
  numberOfDaysToAdd: number
) =>
  z.string().refine(
    (value) => {
      const valueDate = new Date(value);
      const futureDate = new Date(baseDate);
      futureDate.setDate(baseDate.getDate() + numberOfDaysToAdd);
      return valueDate >= baseDate && valueDate <= futureDate;
    },
    {
      message: `Date must be between ${baseDate.toISOString()} and ${numberOfDaysToAdd} days in the future`,
    }
  );
